/**
 * ToolCard — collapsible tool-call surface for AIKO chat.
 *
 * Renders a single agent tool call as a card-shaped row inside an assistant
 * message: a canonical `StatusChip` on the right, label on the left, optional
 * icon leading the label, and a collapsible body that holds the call's `args`
 * and `result`. The card frame stays monochrome (ArtifactTile discipline #162);
 * status reads only from the chip, in the SAME form + color as every artifact.
 *
 * Three statuses drive the visual, mapped onto the shared lifecycle vocabulary
 * (`STATUS_TO_CANONICAL`) so the chip matches the Brain's status language:
 *   - `pending`  — `generating` shimmer chip (label `Running`), body usually
 *                  open so the user can see the tool taking shape live.
 *   - `success`  — `ready` settled success dot (label `Done`), body collapsed
 *                  by default so the assistant's reply remains the focus.
 *   - `error`    — `failed` destructive dot (label `Failed`), body open by
 *                  default so the user immediately sees what went wrong.
 *
 * Body sections are skipped when their slot is empty — passing only `result`
 * yields a card with just one section. Pass `<Markdown content={...} />` for
 * rich text; the primitive only owns layout, not content rendering.
 *
 * Surface: the card wears the Onyx hex-notch silhouette at the card scale
 * (`nx-s`). The two details a `clip-path` would break in the v1 way are handled
 * per the surface recipe (issue #158):
 *   - ELEVATION is a clip-safe `.elev-card` `drop-shadow`, NOT an outer
 *     `box-shadow` — a box-shadow is clipped away by the notch clip-path.
 *   - The OUTLINE is a continuous `nx-rim` RING overlay (the `[border]`-toned
 *     hairline tracing the full notched silhouette including the chamfer), NOT a
 *     real CSS `border` — a border is sliced at the notch diagonal. The body's
 *     interior `border-t` divider is an inner horizontal rule, not the silhouette
 *     outline, so it stays a plain border.
 *
 * Disclosure: the trigger button carries `aria-expanded` + `aria-controls`,
 * the body carries a matching id and `role="region"` with `aria-labelledby`.
 * `prefers-reduced-motion` collapses the disclosure height animation; the
 * StatusChip carries its own reduced-motion guard (the in-progress shimmer
 * holds flat), so status always reads via copy + dot color, not animation.
 *
 * Composition with `MessageRow`:
 *
 *   <MessageRow role="tool">
 *     <ToolCard
 *       icon={<SearchIcon />}
 *       label="search_experiences"
 *       status="success"
 *       args={<pre>{JSON.stringify({ q: "ml" }, null, 2)}</pre>}
 *       result={<Markdown content={`Found **3 experiences** matching ml`} />}
 *     />
 *   </MessageRow>
 */
import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronDown, Wrench } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Truncate } from "./Truncate";
import { StatusChip, type StatusExpressionState } from "./StatusExpression";

/* ============================================================================
 * Types
 * ========================================================================= */

export type ToolCardStatus = "pending" | "success" | "error";

// Monochrome chrome (ArtifactTile discipline #162): the card frame stays neutral
// slate/ink across every status — status reads from the shared `StatusChip` on
// the right, never from a per-status border/background tint. This keeps the tool
// surface in the same "color is signal, not decoration" language as the Brain
// tiles, so a wall of tool calls does not read as a wall of colored boxes.
//
// Surface (#158): `nx-s` clips the hex-notch silhouette and `.elev-card` is the
// clip-safe drop-shadow (a `box-shadow` would be sliced away by the clip). The
// outline is the `nx-rim` ring overlay rendered below, NOT a real `border` (a
// border is cut at the notch chamfer). The chrome stays monochrome — the ring is
// the neutral `[border]` token across every status.
const toolCardVariants = cva(
  cn(
    "group/tool relative w-full bg-card text-card-foreground",
    nx.s,
    "elev-card",
    "transition-colors duration-200 ease-smooth-out",
  ),
  {
    variants: {
      status: {
        pending: "",
        success: "",
        error: "",
      },
    },
    defaultVariants: { status: "pending" },
  },
);

/**
 * Map ToolCard's three tool-call states onto the shared canonical lifecycle
 * vocabulary (#125 / aisquare-ui#162) so tool status reads in the SAME form +
 * color as every artifact: `pending` → `generating` (shimmer in-progress chip),
 * `success` → `ready` (settled success dot), `error` → `failed` (destructive
 * dot). The domain label (`Running` / `Done` / `Failed`) is preserved via the
 * `StatusChip` `label` override, so the chip vocabulary is unified without
 * losing the tool-call wording.
 */
const STATUS_TO_CANONICAL: Record<ToolCardStatus, StatusExpressionState> = {
  pending: "generating",
  success: "ready",
  error: "failed",
};

export interface ToolCardProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof toolCardVariants> {
  /** Tool name. Conventionally the registry key (e.g. `search_experiences`). */
  label: React.ReactNode;
  /** Optional leading icon. Defaults to a small wrench glyph. */
  icon?: React.ReactNode;
  /** Lifecycle state. Drives both the status pill and the chrome tint. */
  status?: ToolCardStatus;
  /** Pre-formatted call arguments (typically a `<pre>{JSON}</pre>` block). */
  args?: React.ReactNode;
  /** Pre-formatted call result (typically `<Markdown />` or a small component). */
  result?: React.ReactNode;
  /**
   * Initial expanded state. Default: open while `pending` or `error`,
   * collapsed once `success` lands so the assistant's reply stays the focus.
   * Pass explicitly to override.
   */
  defaultOpen?: boolean;
  /** Controlled expanded state. Pair with `onOpenChange`. */
  open?: boolean;
  /** Notified when the disclosure toggles. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Disable the height-animate disclosure motion. Default true.
   * `prefers-reduced-motion` forces this off. (The StatusChip's in-progress
   * shimmer is governed by its own reduced-motion guard, not this prop.)
   */
  motion?: boolean;
}

/* ============================================================================
 * Component
 * ========================================================================= */

let toolCardIdCounter = 0;
const nextToolCardId = () => `tool-card-${++toolCardIdCounter}`;

const STATUS_LABEL: Record<ToolCardStatus, string> = {
  pending: "Running",
  success: "Done",
  error: "Failed",
};

export const ToolCard = React.forwardRef<HTMLDivElement, ToolCardProps>(
  (
    {
      label,
      icon,
      status = "pending",
      args,
      result,
      defaultOpen,
      open: controlledOpen,
      onOpenChange,
      motion: enableMotion = true,
      className,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = enableMotion && reduced !== true;

    // Body id — stable across renders for aria-controls / aria-labelledby.
    const reactId = React.useId?.();
    const fallbackIdRef = React.useRef<string | null>(null);
    if (!reactId && fallbackIdRef.current === null) {
      fallbackIdRef.current = nextToolCardId();
    }
    const bodyId = `${reactId ?? fallbackIdRef.current}-body`;
    const labelId = `${reactId ?? fallbackIdRef.current}-label`;

    // Default-open heuristic: open while in-flight or errored; collapse on success.
    const initialOpen = defaultOpen ?? status !== "success";
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    // When a tool transitions pending|error → success, auto-collapse so the
    // assistant's final reply (rendered in a sibling MessageRow) becomes the
    // visual focus. Only fires in uncontrolled mode and only on the transition
    // INTO success — staying in success or moving to a different status leaves
    // the user's last toggle alone. Skips the first run so an initial mount in
    // status=success doesn't fight the `defaultOpen=true` override.
    const prevStatusRef = React.useRef(status);
    React.useEffect(() => {
      const prev = prevStatusRef.current;
      prevStatusRef.current = status;
      if (isControlled) return;
      if (prev !== "success" && status === "success") {
        setUncontrolledOpen(false);
      }
    }, [status, isControlled]);

    const setOpen = React.useCallback(
      (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next);
        onOpenChange?.(next);
      },
      [isControlled, onOpenChange],
    );

    const toggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpen(!isOpen);
    };

    const hasBody = args !== undefined || result !== undefined;

    return (
      <div
        ref={ref}
        data-status={STATUS_TO_CANONICAL[status]}
        data-tool-status={status}
        className={cn(toolCardVariants({ status }), className)}
        {...rest}
      >
        {/* Continuous keyline — a 1px `nx-rim` ring clipped to the notched
            silhouette, NOT a CSS border (which the notch clip-path would slice
            at the chamfer). Monochrome `[border]` token across every status. */}
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0 z-[1] [--nx-rim:hsl(var(--border))]"
          style={{ clipPath: NX_RING_CLIP.s }}
        />
        <button
          type="button"
          onClick={toggle}
          aria-expanded={hasBody ? isOpen : undefined}
          aria-controls={hasBody ? bodyId : undefined}
          disabled={!hasBody}
          className={cn(
            "flex w-full items-center gap-3 px-3 py-2 text-left",
            "transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "ring-offset-background",
            hasBody && "cursor-pointer hover:bg-foreground/[0.02]",
            !hasBody && "cursor-default",
          )}
        >
          <span className="shrink-0 text-muted-foreground" aria-hidden>
            {icon ?? <Wrench className="size-3.5" />}
          </span>
          <Truncate
            id={labelId}
            as="span"
            className="flex-1 min-w-0 font-mono text-xs text-foreground"
          >
            {label}
          </Truncate>
          {/* Shared status vocabulary (#162): the canonical StatusChip carries
              the form + color (generating shimmer / settled success dot /
              destructive failed dot), the tool-call domain word is the `label`
              override. One status language across artifacts and tool calls. */}
          <StatusChip
            status={STATUS_TO_CANONICAL[status]}
            label={STATUS_LABEL[status]}
            size="sm"
            className="shrink-0"
          />
          {hasBody ? (
            <ChevronDown
              aria-hidden
              className={cn(
                "size-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-180",
              )}
            />
          ) : null}
        </button>

        <AnimatePresence initial={false}>
          {hasBody && isOpen ? (
            <motion.div
              key="body"
              id={bodyId}
              role="region"
              aria-labelledby={labelId}
              initial={shouldAnimate ? { height: 0, opacity: 0 } : false}
              animate={
                shouldAnimate ? { height: "auto", opacity: 1 } : { height: "auto", opacity: 1 }
              }
              exit={shouldAnimate ? { height: 0, opacity: 0 } : { height: 0, opacity: 0 }}
              transition={
                shouldAnimate
                  ? {
                      height: { duration: 0.22, ease: [0.32, 0.72, 0, 1] },
                      opacity: { duration: 0.18 },
                    }
                  : { duration: 0 }
              }
              className="overflow-hidden border-t border-border/60"
            >
              {args !== undefined ? <ToolSection title="Args">{args}</ToolSection> : null}
              {result !== undefined ? <ToolSection title="Result">{result}</ToolSection> : null}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  },
);
ToolCard.displayName = "ToolCard";

/* ============================================================================
 * Internals — kept colocated; lifted only if a sibling primitive needs them.
 * ========================================================================= */

function ToolSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-3 py-2 first:pt-2 last:pb-2 space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {title}
      </div>
      <div className="text-xs text-foreground/90">{children}</div>
    </div>
  );
}
